import { createContext, useContext, useEffect, useReducer } from 'react';

import { getBackgroundColor } from 'src/core/domain/avatarColors';
import { DEFAULT_NAME } from 'src/core/domain/constants';
import type { Author } from 'src/mapper/threadMapper';
import type { CareerHubProfile } from 'src/mapper/userMapper';
import {
  getSeekMaxLearningProfile,
  getSeekMaxProfile,
} from 'src/services/userService';

import { useAuthStatus } from './auth';
import { useConfig } from './config';

interface ProfileState {
  profile: CareerHubProfile;
  author: Author;
  isLoading: boolean;
  error: Error | null;
}

type ProfileAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; profile: CareerHubProfile; author: Author }
  | { type: 'FETCH_ERROR'; error: Error };

const initialState: ProfileState = {
  profile: {
    id: '',
    email: '',
    firstName: DEFAULT_NAME,
    lastName: '',
    jobTitle: '',
    hashcode: '',
    hashcodeInt: 0,
  },
  author: {
    id: '',
    learningProfileHashCode: 0,
    firstName: DEFAULT_NAME,
    jobTitle: '',
    groups: [],
    isVerified: false,
    avatarColor: '',
    avatarCharacter: DEFAULT_NAME[0],
  },
  isLoading: true,
  error: null,
};

function profileReducer(
  state: ProfileState,
  action: ProfileAction,
): ProfileState {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        profile: action.profile,
        author: action.author,
        error: null,
      };
    case 'FETCH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.error,
        profile: {
          id: '',
          email: '',
          firstName: DEFAULT_NAME,
          lastName: '',
          jobTitle: '',
          hashcode: '',
          hashcodeInt: 0,
        },
        author: {
          id: '',
          learningProfileHashCode: 0,
          firstName: DEFAULT_NAME,
          jobTitle: '',
          groups: [],
          isVerified: false,
          avatarColor: '',
          avatarCharacter: DEFAULT_NAME[0],
        },
      };
    default:
      return state;
  }
}

interface ProfileContextType {
  state: ProfileState;
  dispatch: React.Dispatch<ProfileAction>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: React.ReactNode;
}

const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const [state, dispatch] = useReducer(profileReducer, initialState);
  const { authStatus } = useAuthStatus();
  const config = useConfig();
  const isAuthenticated = authStatus === 'authenticated';
  useEffect(() => {
    const initialize = async () => {
      try {
        dispatch({ type: 'FETCH_START' });

        const [seekMaxProfile, learningProfile] = await Promise.all([
          getSeekMaxProfile(config),
          getSeekMaxLearningProfile(),
        ]);

        const careerHubProfile: CareerHubProfile = {
          id: learningProfile.id,
          email: seekMaxProfile.email,
          firstName: seekMaxProfile.firstName,
          lastName: seekMaxProfile.lastName,
          jobTitle: seekMaxProfile.jobTitle,
          hashcode: learningProfile.hashcode,
          hashcodeInt: learningProfile.hashcodeInt,
        };

        const author: Author = {
          id: learningProfile.id,
          learningProfileHashCode: learningProfile.hashcodeInt,
          firstName: seekMaxProfile.firstName,
          jobTitle: seekMaxProfile.jobTitle,
          groups: [],
          isVerified: false,
          avatarColor: getBackgroundColor(learningProfile.hashcodeInt),
          avatarCharacter: seekMaxProfile.firstName.charAt(0),
        };

        dispatch({
          type: 'FETCH_SUCCESS',
          profile: careerHubProfile,
          author,
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_ERROR',
          error:
            err instanceof Error ? err : new Error('Failed to fetch profile'),
        });
      }
    };

    if (isAuthenticated) {
      initialize();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <ProfileContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export { ProfileProvider, useProfile };
